import axios from "../../../../services/axios";
import { useReducer, useEffect } from "react";
import { School, DbUser } from "../../../../interfaces";
import { Role } from "../../../../enums";
import { Class } from "@backend-types";
import { ClassExtended } from "../../../../interfaces";
import { EditClassProps } from "../EditDetails/EditClass";
import { isAfter } from "date-fns";
import { logger } from "../../../../services/logger";

export type ClassEditState = {
  form: {
    teacherId: string;
    schoolId: string;
    courseId: number | null | undefined;
    period: string;
    startDate: Date | null;
    endDate: Date | null;
    isTester: boolean;
  };
  data: {
    schools: {
      value: School[];
      succeeded: boolean;
    };
    teachers: {
      value: DbUser[];
      succeeded: boolean;
    };
    classes: {
      value: Class[];
      succeeded: boolean;
    };
  };
  currentUser: DbUser;
  class?: ClassExtended;
};

export type UpdateFormAction = {
  type: "updateForm";
  update: Partial<ClassEditState["form"]>;
  extendedClass?: ClassExtended;
};
export type ReplaceStateAction = {
  type: "replaceState";
  state: ClassEditState;
};
export type DataSuccessAction<T extends keyof ClassEditState["data"]> = {
  type: "dataSuccess";
  key: T;
  value: ClassEditState["data"][T]["value"];
};

export type Action =
  | UpdateFormAction
  | ReplaceStateAction
  | DataSuccessAction<keyof ClassEditState["data"]>;

export const initClassEditState = (
  currentUser: DbUser,
  extendedClass?: ClassExtended,
  data?: ClassEditState["data"]
): ClassEditState => {
  const state: ClassEditState = {
    form: {
      courseId: 1,
      period: "",
      startDate: null,
      endDate: null,
      teacherId: "",
      schoolId: "",
      isTester: false,
    },
    data: data ?? {
      schools: {
        value: [],
        succeeded: false,
      },
      teachers: {
        value: [],
        succeeded: false,
      },
      classes: {
        value: [],
        succeeded: false,
      },
    },
    currentUser,
    class: extendedClass,
  };
  // Admins can only edit to their own school
  if ([Role.admin].includes(currentUser.role)) {
    state.form.schoolId = String(currentUser.user_school[0].school_id);
    state.data.schools.value = currentUser.user_school[0].school
      ? [currentUser.user_school[0].school]
      : [];
  }

  return state;
};
export const dataSuccess = <T extends keyof ClassEditState["data"]>({
  key,
  value,
}: Omit<DataSuccessAction<T>, "type">): DataSuccessAction<T> => ({
  type: "dataSuccess",
  key,
  value,
});

function reducer(state: ClassEditState, action: Action): ClassEditState {
  switch (action.type) {
    case "replaceState":
      return action.state;
    case "updateForm": {
      const { update, extendedClass } = action;
      const nextForm = { ...state.form, ...update };

      if (
        String(nextForm.schoolId) !== String(extendedClass?.school_id ?? "") &&
        nextForm.schoolId !== state.form.schoolId
      ) {
        nextForm.teacherId = "";
      }

      return {
        ...state,
        form: nextForm,
        class: extendedClass,
      };
    }
    case "dataSuccess": {
      return {
        ...state,
        data: {
          ...state.data,
          [action.key]: {
            value: action.value,
            succeeded: true,
          },
        },
      };
    }
  }
}

export function getError(state: ClassEditState): string | undefined {
  if (state.form.startDate !== null && state.form.endDate !== null) {
    if (isAfter(state.form.startDate, state.form.endDate)) {
      return "The end date cannot be before start date.";
    }
  }
}

export function useClassEditState(props: EditClassProps) {
  const [state, dispatch] = useReducer(
    reducer,
    initClassEditState(props.currentUser, props.class)
  );

  useEffect(() => {
    if (!props.open) return;
    if (props.currentUser.role !== Role.super) return;
    const getSchools = async () => {
      try {
        const res = await axios.get<School[]>("/schools");
        dispatch(dataSuccess({ key: "schools", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getSchools();
  }, [props.currentUser.role, state.class, props.open]);

  useEffect(() => {
    if (!props.open) return;

    // Wait to get teachers until school is chosen
    if (
      !(state.form.schoolId && [Role.super, Role.admin].includes(props.currentUser.role))
    )
      return;

    const getTeachers = async () => {
      try {
        const res = await axios.get<DbUser[]>("/teachers", {
          params: { schoolId: state.form.schoolId },
        });
        dispatch(dataSuccess({ key: "teachers", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getTeachers();
  }, [props.currentUser.role, state.form.schoolId, props.open]);

  return [state, dispatch] as const;
}
